import React, { memo, Reducer, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { ProgressSteps, NewModal, MainContentPaddingTypes, ReportModalLvl1, ReportModalLvl2, ReportModalLvl3, WaitAnimation, NewButton, Overlay, NewButtonList } from "../..";
import { ButtonTypes, ReportData, ReportTemplate } from "../../../Definitions";
import { useAppSelector } from "../../../Store";
import { Graph_QL, useReport, useReportTemplate } from "../../../Services";
import { useNavigate } from "react-router";
import { NavigationFunction as RootNavigation, Pages } from "../../../Navigation/root";
import { IReportContext, ReportContext, ReportDispatcherT, ReportModalTypes, ReportReducer } from "../_Reports/reportModalContext";
import REPORT_TEMPLATE_BAR from "../reportTemplateBar"

// #region | TemplateList
const TemplateList = memo(({ templates, onCheck, selectedTemplateIds, deletable, editable, onChange }: {
  templates: ReportTemplate[],
  onCheck: (templateId: number) => void,
  onChange: (template: ReportTemplate) => void,
  selectedTemplateIds: number[],
  deletable: boolean,
  editable: boolean
}) => (
  <>
    <REPORT_TEMPLATE_BAR headerBar borderRadius={{borderRadius: templates.length === 0 ? "10px" : "10px 10px 0px 0px"}}/> 
    {templates.map((t, index) => {
      const templateId = t.templateId

      if (!templateId) return null;

      const isSelected = selectedTemplateIds.includes(templateId);

      return (
        <REPORT_TEMPLATE_BAR
          key={t.templateId}
          template={t}
          editable={editable}
          deletable={deletable}
          borderRadius={{ borderRadius: index === templates.length - 1 ? "0px 0px 10px 10px" : "0px" }}
          onCheck={() => onCheck(templateId)}
          isChecked={isSelected}
          onChange={onChange}
        />
      )
    })}
  </>
))
// #endregion

interface TemplateModal_Props {
  isModalOpen: boolean
  closeModal: () => void,
  modalType: ReportModalTypes,
  readonly template?: ReportTemplate 
}

export default function ReportModal({ isModalOpen, closeModal, modalType, template }: TemplateModal_Props) {
  const navigator = useNavigate();
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: 793px)`);
  
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const {domain, connection} = useMemo(() => {
    let domain = Graph_QL.ObjToString({ domain: farm })
    let connection = Graph_QL.create({ domain: farm })
  return {domain, connection}}, [farm]);
  const [loading, setLoading] = useState(false);

  const [_state, dispatch] = useReducer<Reducer<IReportContext, ReportDispatcherT>>(ReportReducer, {
    farm: farm,
    domain: domain,
    connection: connection,
    currentViewLevel: 1,
    currentViewType: modalType,
    report: {
      name: undefined,
      archived: true,
      startDate: undefined,
      endDate: undefined,
      dataLevel: undefined,
      filterParameter: [[]]
    } as ReportData,
    template: {
      name: undefined,
      dataLevel: undefined,
      filterParameter: [[]]
    } as ReportTemplate,
    error: { Elementid: 0, errorText: "" }
  } as IReportContext)

  useEffect(() => {
    if (isModalOpen && template && _state.currentViewType === ReportModalTypes.createReport) {
      dispatch({
        report: {
          dataLevel: template.dataLevel,
          filterParameter: template.filterParameter
        }
      })
    }
    if (!isModalOpen) {
      dispatch({ reset: true });
    }
  }, [_state.currentViewType, isModalOpen, template])

  const _reportModal = _state.currentViewType === ReportModalTypes.createReport
  const _templateModal = _state.currentViewType === ReportModalTypes.createTemplate || _state.currentViewType === ReportModalTypes.copyTemplate

  // #region | Templates manage handle
  const [currentTemplateView, setCurrentTemplateView] = useState<"ADD" | "MANAGE">("ADD");
  const [templateManagemode, setTemplateManageMode] = useState<"BASIC" | "EDIT" | "DELETE">("BASIC")
  const updatedWTemplateRef = useRef<Map<number,ReportTemplate>>(new Map());
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([]);
  const { call: getTemplates, data: templateState, maintainEntries: maintainTemplate } = useReportTemplate(connection);

  useEffect(() => {
    getTemplates({fetchAmount: 50, offset: 0});
  },[getTemplates])
  
  const templates = useMemo(() => {
    if (templateState && templateState[domain]) {
      return templateState[domain].data
    }
    return [];
  }, [domain, templateState])

  const singleTemplateCheck = useCallback((templateId: number) => {
    setSelectedTemplateIds((prev) =>
      prev.includes(templateId) ? prev.filter((id) => id !== templateId) : [...prev, templateId]
    );
  }, [])
  
  const onTemplateChange = (template: ReportTemplate) => {
    if (!template || !template.templateId) return;
    updatedWTemplateRef.current.set(template.templateId, template);
  }

  const handleTemplateUpdate = () => {
    const updatedTemplate = Array.from(updatedWTemplateRef.current.values());
    if (updatedTemplate.length && maintainTemplate) {
      updatedTemplate.forEach((data) => {maintainTemplate({data})})
    }
  }

  const handleTemplateDelete = () => {
    if (!maintainTemplate) return
  
    maintainTemplate(undefined, { data: selectedTemplateIds });
  }
  // #endregion

  // #region | Handle save report or template
  const { maintainEntries: maintainReport } = useReport(connection);

  const handleSaveReport = async () => {
    const source = _reportModal ? _state.report : _templateModal ? _state.template : null;
    if (!source) return;
    
    const handleTimeout = (elementId: number) => {
      setTimeout(() => {
        dispatch({ error: { Elementid: elementId, errorText: "" } });
      }, 2000)
    }

    if (_state.currentViewLevel === 1) {
      if (!source.name) {
        return (
          dispatch({ currentViewLevel: 1, error: { Elementid: 1, errorText: (t("reportPages.errorMessages.missingName", {defaultValue: "missing name"})) } }),
          handleTimeout(1)
        )
      }
      if (_reportModal && !_state.report.startDate) {
        return (
          dispatch({ currentViewLevel: 1, error: { Elementid: 2, errorText: (t("reportPages.errorMessages.missingStartDate", {defaultValue: "missing start date"})) } }),
          handleTimeout(2)
        )
      }
      if (_reportModal && !_state.report.endDate) {
        return (
          dispatch({ currentViewLevel: 1, error: { Elementid: 3, errorText: (t("reportPages.errorMessages.missingEndDate", {defaultValue: "missing end date"})) } }),
          handleTimeout(3)
        )
      }
      if (!source.dataLevel) {
        return (
          dispatch({ currentViewLevel: 1, error: { Elementid: 4, errorText: (t("reportPages.errorMessages.missingDataLevel", { defaultValue: "missing data level" })) } }),
          handleTimeout(4)
        )
      }
      dispatch({ currentViewLevel: _state.currentViewLevel + 1 });
      return;
    }

    if (_state.currentViewLevel === 2) {
      
      if (!source.filterParameter || source.filterParameter.length === 0 || source.filterParameter[0].length === 0 || source.filterParameter[0].some(param => !param.key || !param.value || param.value.length === 0)) {
        return (
          dispatch({ currentViewLevel: 2, error: { Elementid: 5, errorText: (t("reportPages.errorMessages.missingFilterParameters", {defaultValue: "missing filter parameters"})) } }),
          handleTimeout(5)
        )
      }
      dispatch({ currentViewLevel: _state.currentViewLevel + 1 });
      return;
    }
    
    if (_reportModal && _state.currentViewLevel === 3 && _state.report && maintainReport) {
        setLoading(true);
        const affectedIds = await maintainReport(undefined, undefined, { data: { ..._state.report, endDate: _state.report.endDate ? _state.report.endDate / 1000 : undefined, startDate: _state.report.startDate ? _state.report.startDate / 1000 : undefined, archived: true } })
        setLoading(false);
      
      if (affectedIds && !(affectedIds[0] === undefined || affectedIds[0] === null)) {
          RootNavigation({ Navigator: navigator, navigation_page: Pages.reports, navigation_tab: Pages.report, replace: true, navArgs: { selected: affectedIds[0] }, currentDomain: farm });
          closeModal();
        }
    }

    if (_templateModal && _state.currentViewLevel === 3 && _state.template && maintainTemplate) {
      maintainTemplate(undefined, undefined, { data: { ..._state.template, shouldArchive: false } })
      closeModal();
    }
  }
  // #endregion

  // Handle go back
  const goBack = () => {
    if (_state.currentViewLevel > 1) dispatch({currentViewLevel:_state.currentViewLevel-1})
  };
  
  return (
    <ReportContext.Provider value={{state:_state, dispatch}}>
      <NewModal 
        modalState={isModalOpen}
        overrides={{ 
          mainContentPadding: currentTemplateView !== "MANAGE" ? "20px" : MainContentPaddingTypes.primary,
          headerContentHeight: "65px",
          headerHeight: !template ? "80px" : undefined,
          secondaryContentScroll: true,
          mainContentScroll: currentTemplateView !== "MANAGE" ? false : true,
          backgroundColor: currentTemplateView !== "MANAGE" ? undefined : colors.greyLightTertiary
        }}
        predefinedButtons={{
          onClose: closeModal,
          onGoBack: _state.currentViewLevel > 1 ? goBack : undefined
        }}
        tabButtons={{
          contents: _reportModal ? [] : [t("buttons.add", {defaultValue: "add"}), t("reportPages.manageTemplates.manageTemplates", {defaultValue: "manage"})],
          onClick: (i) => {
            if (i === 0) { setCurrentTemplateView("ADD") }
            if (i === 1) { setCurrentTemplateView("MANAGE") }
          },
          startEnhancers: [glyphs.Add({}), glyphs.Edit({})],
          disabled: currentTemplateView === "ADD" ? 0 : 1,
          active: currentTemplateView === "ADD" ? 0 : 1
        }}
        header={_templateModal ? t("reportPages.manageTemplates.templateOptions", { defaultValue: "template options" }) : t("reportPages.reports.newReport", { defaultValue: "new report" })}
        subHeader={template ? `${t("reportPages.reports.fromTemplate", {defaultValue: "from template"})} ${template.name}` : ""}
        headerContent={ currentTemplateView !== "MANAGE" ?
          <ProgressSteps 
            titles={[t("reportPages.reports.dataLevel", {defaultValue: "data level"}), t("reportPages.reports.filterParameter", {defaultValue: "filter parameter"}), t("reportPages.reports.dataParameter", {defaultValue: "data parameter"})]} 
            icons={[glyphs.Animal({}), glyphs.Animal({}), glyphs.Animal({})]}
            opacityValues={_state.currentViewLevel === 1 ? [1, 0.5, 0.5] : _state.currentViewLevel === 2 ? [0.5, 1, 0.5] : [0.5, 0.5, 1]}
          /> : undefined
        }
        mainContent={currentTemplateView !== "MANAGE" && _state.currentViewLevel !== 2
          ? <div style={{ width: mobileXs ? "290px" : "605px", margin: "0 auto" }}>
            {_state.currentViewLevel === 1 && <ReportModalLvl1 templates={templates}/>}
            {_state.currentViewLevel === 3 && <ReportModalLvl3 />}
            {loading && <WaitAnimation color={colors.greyLightTertiary} />}
          </div>
          : currentTemplateView === "MANAGE" ? templates.length === 0 ? <p style={{ textAlign: "center", color: colors.greyDarkPrimary, ...fonts.fontSize4, ...fonts.paragraph }}>{t("reportPages.manageTemplates.noTemplates", { defaultValue: "There is at the moment no templates on this page." })}</p> : <>
            <div style={{ display: "flex", justifyContent: templateManagemode === "DELETE" ||templateManagemode === "EDIT"? "end" : "space-between", alignItems: "end", width: "650px", padding: "0px 20px", boxSizing: "border-box", zIndex: templateManagemode === "DELETE" ? "9999" : undefined, position: templateManagemode === "DELETE" ? "relative" : undefined }}>
              {templateManagemode === "DELETE" ||templateManagemode === "EDIT" ? <></> : (
                <p style={{ ...css.penHeaders }}>
                  {t("locationFeederPages.sections.penValueHeaders", { defaultValue: "Hold the mouse on a value to see column type" })}
                </p>
              )}
              {templateManagemode === "DELETE" ||templateManagemode === "EDIT" ? (
                <NewButtonList
                  contents={[t("buttons.save", { defaultValue: "save" }), t("buttons.cancel", { defaultValue: "cancel" }), t("buttons.edit", {defaultValue: "edit"}), t("systemControls.general.delete", { defaultValue: "delete" })]} 
                  onClick={(i => {
                    if (i === 0) { templateManagemode === "DELETE" ? handleTemplateDelete() : handleTemplateUpdate(); setTemplateManageMode("BASIC") }
                    if (i === 1) {setTemplateManageMode("BASIC")}
                  })}
                  buttonType={ButtonTypes.tableButton}
                  startEnhancers={[
                    glyphs.Check({fillColor: colors.greyDarkPrimary}),
                    glyphs.Close({ fillColor: colors.greyDarkPrimary }),
                    glyphs.Edit({fillColor: colors.greyDarkPrimary}),
                    glyphs.Delete({fillColor: colors.greyDarkPrimary})
                  ]}
                  disabled={[false, false, true, true]}
                  overrides={(index) => ({
                    baseStyles: {
                      backgroundColor: (templateManagemode === "DELETE" && index === 3) || (templateManagemode === "EDIT" && index === 2)
                        ? colors.greyLightSecondary
                        : colors.greyLightPrimary,
                      opacity: (templateManagemode === "DELETE" && index === 2) || (templateManagemode === "EDIT" && index === 3)
                        ? "0.5"
                        : "1" 
                    },
                    hoverStyles: {
                      backgroundColor: colors.greyLightSecondary
                    },
                    contentStyles: {
                      color: colors.greyDarkPrimary
                    }
                  })}
                />
              ) : (
                <NewButtonList
                  contents={[t("buttons.edit", {defaultValue: "edit"}), t("systemControls.general.delete", { defaultValue: "delete" })]} 
                  onClick={(i) => {
                    if (i === 0) { setTemplateManageMode("EDIT") }
                    if (i === 1) { setTemplateManageMode("DELETE") }
                  }}
                  buttonType={ButtonTypes.tableButton}
                  startEnhancers={[glyphs.Edit({fillColor: colors.greyDarkPrimary}), glyphs.Delete({fillColor: colors.greyDarkPrimary})]}
                  overrides={{
                    baseStyles: { backgroundColor: colors.greyLightPrimary },
                    hoverStyles: { backgroundColor: colors.greyLightSecondary },
                    contentStyles: { color: colors.greyDarkPrimary }
                  }}
                />
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <TemplateList
                templates={templates}
                onCheck={singleTemplateCheck}
                onChange={onTemplateChange}
                editable={templateManagemode === "EDIT"}
                deletable={templateManagemode === "DELETE"}
                selectedTemplateIds={selectedTemplateIds}
              />
            </div> 
            {templateManagemode === "DELETE" ? <Overlay overrides={{ color: colors.transparent }} beforeUnload onClickAlert /> : <></>}
          </>
        : undefined}

        secondaryContent={ currentTemplateView !== "MANAGE" && _state.currentViewLevel === 2 ?
          <div style={{ width: mobileXs ? "290px" : "605px" }}>
            {_state.currentViewLevel === 2 && <ReportModalLvl2 />}
            {loading && <WaitAnimation color={colors.greyLightTertiary} />}
          </div> : undefined
        }
        footerContent={ currentTemplateView !== "MANAGE" ?
          <NewButton 
            content={_state.currentViewLevel < 3 ? t("buttons.next", {defaultValue: "next"}) : t("buttons.create", {defaultValue: "create"})}
            onClick={handleSaveReport}
          />
        : undefined} 
        footerMessage={_state.error.errorText}
      />
    </ReportContext.Provider>
  );
}